// ** Modules
import React from 'react';
import { useTranslation } from 'next-i18next/pages';

// ** UI
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';

// ** Components
import Button from '@/components/common/Button';

// ** Types
import type { Theme } from '@mui/material/styles';

type LoaderProps = {
	retryLogin?: () => void;
};

const getServerInjectedTranslation = (keyPath: string) => {
	// keyPath like "common.loading_in_progress" or the final key 'loading_in_progress'
	try {
		if (typeof window === 'undefined') return undefined;
		// next-i18next attaches initial store under pageProps._nextI18Next.initialI18nStore
		const nextData = (window as any).__NEXT_DATA__;
		const pageProps = nextData?.props?.pageProps;
		const nextI18Next = pageProps?._nextI18Next;
		if (!nextI18Next) return undefined;

		const locale = nextI18Next.initialLocale;
		const store = nextI18Next.initialI18nStore;
		if (!locale || !store) return undefined;

		// split keyPath: e.g. "common.loading_in_progress"
		const [ns, key] = keyPath.includes('.') ? keyPath.split('.') : ['common', keyPath];

		return store?.[locale]?.[ns]?.[key];
	} catch {
		return undefined;
	}
};

const Loader = ({ retryLogin }: LoaderProps) => {
	// Media query
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'), { noSsr: true });

	// State
	const [showRetryButton, setShowRetryButton] = React.useState(false);

	// Try to get the exact translation the server used (prevents hydration mismatch)
	const serverText = React.useMemo(() => getServerInjectedTranslation('common.loading_in_progress'), []);

	// Translation
	const { t } = useTranslation('common');

	// Use serverText when available (ensures exact same string as server),
	// otherwise use the i18n hook.
	const displayText = serverText ?? t('loading_in_progress');

	// Show the retry button after 5 seconds (only for mobile)
	React.useEffect(() => {
		if (!isMobile) return;

		const timer = setTimeout(() => {
			setShowRetryButton(true);
		}, 5000);

		return () => clearTimeout(timer);
	}, [isMobile]);

	return (
		<Grid
			container
			sx={{
				height: '100vh',
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center'
			}}
		>
			<Grid>
				{!showRetryButton ? (
					<React.Fragment>
						<CircularProgress color="primary" size={64} />
						<Typography
							// If initial store was not available, we allow React to suppress the hydration warning.
							// But if serverText exists, this will be identical and no warning occurs.
							suppressHydrationWarning={!serverText}
							sx={{
								color: (theme) => theme.palette.text.primary,
								marginLeft: (theme) => theme.spacing(1)
							}}
						>
							{displayText}
						</Typography>
					</React.Fragment>
				) : (
					<Button variant="contained" color="secondary" onClick={retryLogin} sx={{ marginTop: 2 }}>
						Retry Login
					</Button>
				)}
			</Grid>
		</Grid>
	);
};

export default Loader;
